import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Autocomplete,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Avatar,
  Checkbox,
  FormGroup,
  FormHelperText,
  ListItemText,
  OutlinedInput,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Divider,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  PhotoCamera as PhotoIcon,
  Settings as SettingsIcon,
  CheckCircle as EnabledIcon,
  Cancel as DisabledIcon,
  GridView as GridViewIcon,
  TableRows as TableRowsIcon,
  Search as SearchIcon,
  BookmarkAdded as BookmarkAddedIcon,
  WorkspacePremium as PremiumIcon,
  Stars as StarIcon,
  ReportProblem as WarningIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { modelFaceAPI, middlewareDataAPI } from '../../services/api';
import { API_URLS } from '../../config/apiConfig';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Optional size matches backend: 400x400 / 800x800 / 1200x1200 (JPEG thumbnails on S3 next to the master image).
const getModelFaceImageUrl = (img, size = null) => {
  if (!img || !img.id) return '';
  const base = `${API_URLS.ADMIN_BASE}/model-face/images/${img.id}/file`;
  // Cache-bust when image row is updated so replaced uploads show immediately in grid/popup.
  const version = encodeURIComponent(img.updated_at || img.created_at || img.image_path || img.id);
  if (size && ['400x400', '800x800', '1200x1200'].includes(size)) {
    return `${base}?size=${encodeURIComponent(size)}&v=${version}`;
  }
  return `${base}?v=${version}`;
};

// Matches catalog validity_days semantics: null / 0 / empty = lifetime; N > 0 = access window length.
const formatCatalogValidityLabel = (vd) => {
  if (vd == null || vd === '') return 'Lifetime (no expiry)';
  const n = Number(vd);
  if (!Number.isFinite(n) || n <= 0) return 'Lifetime (no expiry)';
  return `${n} day${n === 1 ? '' : 's'} after booking or assignment`;
};

// Backend stores expires_at as assigned_at + N×24h; UI shows last inclusive calendar day (N-day window with assignment day as day 1 = cutoff date minus one calendar day).
const formatLastInclusiveAccessDate = (expiresAtIso) => {
  if (!expiresAtIso) return null;
  const d = new Date(expiresAtIso);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - 1);
  // Last inclusive access day shown as DD/MM/YYYY in IST
  return d.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

// Human-readable labels for ai_model_addon_logs.action_type
const AI_MODEL_ACTION_LABELS = {
  catalog_created: 'Catalog created',
  catalog_updated: 'Catalog updated',
  catalog_soft_deleted: 'Catalog deleted',
  bulk_import_row: 'Bulk Excel import',
  admin_assignment: 'Admin assignment',
  admin_bulk_assignment: 'Admin bulk assign',
  assignment_revoked: 'Assignment revoked',
  merchant_booking: 'Merchant purchase',
  addon_merchant_settings_updated: 'Merchant addon settings',
  addon_service_created: 'Addon service created',
  addon_service_updated: 'Addon service updated',
  addon_service_deleted: 'Addon service deleted',
};

// Shared header cell style for the activity history table
const auditLogHeadCellSx = {
  fontWeight: 700,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'text.secondary',
  bgcolor: 'grey.100',
  borderBottom: '2px solid',
  borderColor: 'divider',
  py: 1.25,
};

// Same contact idea as MerchantManagement: email when present; else show code + local number like "+91 98765…".
const formatMerchantAutocompleteLabel = (o) => {
  if (!o) return '';
  if (o.email) {
    return `${o.name || '—'} (${o.email})`;
  }
  const phone =
    (o.mobile_code && o.mobile ? `${o.mobile_code} ${o.mobile}` : null) ||
    (o.whatsapp_code && o.whatsapp_number ? `${o.whatsapp_code} ${o.whatsapp_number}` : null) ||
    o.mobile ||
    o.whatsapp_number ||
    '—';
  return `${o.name || '—'} (${phone})`;
};

// Normalize model-face list responses (axios body may be { data, total } or legacy shapes).
const readPaginatedModelFacePayload = (res) => {
  const p = res?.data;
  if (!p || typeof p !== 'object') return { rows: [], total: 0 };
  const rows = Array.isArray(p.data) ? p.data : [];
  const total = typeof p.total === 'number' ? p.total : rows.length;
  return { rows, total };
};

/** Admin UI copy: show counts as “token” / “tokens” (API fields may still say credit_*). */
const formatTokenAmount = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x} ${x === 1 ? 'token' : 'tokens'}`;
};

// Per-field validation messages under inputs in the Add/Edit catalog model dialog (not a global snackbar).
const INITIAL_IMAGE_FORM_ERRORS = {
  name: '',
  display_name: '',
  gender_option_id: '',
  ethnicity_id: '',
  sort_order: '',
  credit_cost: '',
  validity_days: '',
  portrait: '',
};

const AddonServices = () => {
  const [tabValue, setTabValue] = useState(1);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Model catalog tab (portrait grid/table) UX state
  const [imageViewMode, setImageViewMode] = useState('grid');
  const [imageSearch, setImageSearch] = useState('');
  const [imageGenderFilter, setImageGenderFilter] = useState('');
  const [imageEthnicityFilter, setImageEthnicityFilter] = useState('');
  const [imageActiveFilter, setImageActiveFilter] = useState('');
  // '' = all, 'booked' = availability_status 2, 'available' = not booked.
  const [imageAvailabilityFilter, setImageAvailabilityFilter] = useState('');
  const [imageSortBy, setImageSortBy] = useState('display_name');
  const [imageSortDir, setImageSortDir] = useState('asc');
  const [imagePreview, setImagePreview] = useState({ open: false, image: null });
  // Detail popup: GET /model-face/images/:id/usage-summary (generation counts + validity_days).
  const [imagePreviewUsage, setImagePreviewUsage] = useState({
    loading: false,
    completed: null,
    total: null,
    validityDays: null,
    error: null,
  });
  // Popup: full audit trail for one catalog model (backend: GET /model-face/audit-logs).
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogImage, setAuditLogImage] = useState(null);
  const [auditLogRows, setAuditLogRows] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogPage, setAuditLogPage] = useState(0);
  const [auditLogTotal, setAuditLogTotal] = useState(0);
  const auditLogPerPage = 15;
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, image: null, loading: false });

  // ────── Images Tab State ──────
  const [images, setImages] = useState([]);
  const [imagePage, setImagePage] = useState(0);
  // Keep table pagination defaulted to 12 rows across AI face management views.
  const [imageRowsPerPage, setImageRowsPerPage] = useState(12);
  const [imageTotal, setImageTotal] = useState(0);
  const [imageDialog, setImageDialog] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Blocks double-submit on Create / Save while multipart API is in flight.
  const [imageSaveLoading, setImageSaveLoading] = useState(false);
  const [imageFormErrors, setImageFormErrors] = useState(() => ({ ...INITIAL_IMAGE_FORM_ERRORS }));

  // If a model is already booked by a merchant (availability_status=2 + booked_by_user_id),
  // keep pricing/identity-related fields read-only during admin edits.
  const isBookedReadOnly = Boolean(
    editingImage &&
    Number(editingImage.availability_status) === 2,
  );
  
  // Bulk Upload State
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);

  const [imageForm, setImageForm] = useState({
    name: '', display_name: '', description: '', gender_option_id: '',
    ethnicity_id: '', is_active: true, sort_order: 0,
    credit_cost: '',
    // Days of merchant access after booking; 0 or backend empty mapping = lifetime (no expiry).
    validity_days: '',
  });

  // ────── Assignments Tab State ──────
  const [assignments, setAssignments] = useState([]);
  const [assignPage, setAssignPage] = useState(0);
  const [assignRowsPerPage, setAssignRowsPerPage] = useState(12);
  const [assignTotal, setAssignTotal] = useState(0);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({ user_id: null, model_face_image_ids: [] });
  // Second step: show merchant balance, line items, total, then deduct on confirm.
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);
  const [assignBalanceLoading, setAssignBalanceLoading] = useState(false);
  const [assignBalance, setAssignBalance] = useState(null);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [assignmentPreview, setAssignmentPreview] = useState({ open: false, row: null });
  // MUI confirm for revoking a user↔portrait assignment (replaces window.confirm; works inside nested dialogs).
  const [revokeAssignmentConfirm, setRevokeAssignmentConfirm] = useState({
    open: false,
    assignment: null,
    merchantName: '',
    loading: false,
  });
  // User Assignments tab: debounced search (name, email, company, mobile, WhatsApp).
  const [assignmentSearchInput, setAssignmentSearchInput] = useState('');
  const [assignmentSearchDebounced, setAssignmentSearchDebounced] = useState('');

  // ────── Config Tab State ──────
  const [enabledUsers, setEnabledUsers] = useState([]);
  const [addonServices, setAddonServices] = useState([]); // Master data for services
  const [configPage, setConfigPage] = useState(0);
  const [configRowsPerPage, setConfigRowsPerPage] = useState(12);
  const [configTotal, setConfigTotal] = useState(0);
  // Merchant addon table: debounced search sent to GET /enabled-users as `search`.
  const [merchantAddonSearchInput, setMerchantAddonSearchInput] = useState('');
  const [merchantAddonSearchDebounced, setMerchantAddonSearchDebounced] = useState('');
  const [configDialog, setConfigDialog] = useState(false);
  const [configUserSearch, setConfigUserSearch] = useState([]);
  const [configUserSearchLoading, setConfigUserSearchLoading] = useState(false);
  const [selectedConfigUser, setSelectedConfigUser] = useState(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]); // For dialog selection
  // True when opening from a table row: user is pre-filled and we sync all addon toggles on save.
  const [configDialogPrefilled, setConfigDialogPrefilled] = useState(false);
  // Auto-select default addon once for first-time merchant configuration.
  const configDefaultAppliedRef = useRef(false);

  const defaultModelFaceAddonId = useMemo(() => {
    // Prefer the canonical service_type, with a name fallback for older datasets.
    const byType = (addonServices || []).find((s) => String(s?.service_type).toLowerCase() === 'model_face');
    if (byType?.id != null) return byType.id;
    const byName = (addonServices || []).find((s) => String(s?.name || '').toLowerCase().includes('ai model studio'));
    return byName?.id ?? null;
  }, [addonServices]);

  // ────── Dropdown Data ──────
  const [ethnicities, setEthnicities] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [allImages, setAllImages] = useState([]);

  // ──────────────────────────────────────────────
  // DATA FETCHERS
  // ──────────────────────────────────────────────

  const loadDropdowns = useCallback(async () => {
    try {
      const [ethRes, genRes] = await Promise.all([
        modelFaceAPI.getEthnicities(),
        middlewareDataAPI.getGenderOptions(),
      ]);
      setEthnicities(ethRes.data?.data || []);
      setGenderOptions(genRes.data?.data || []);
    } catch (err) {
      console.error('Failed to load dropdowns:', err);
    }
  }, []);

  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await modelFaceAPI.getImages({
        page: imagePage + 1,
        per_page: imageRowsPerPage,
      });
      setImages(res.data?.data || []);
      setImageTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Failed to load images:', err);
      setSnackbar({ open: true, message: 'Failed to load model catalog', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [imagePage, imageRowsPerPage]);

  const loadAllImages = useCallback(async () => {
    try {
      const res = await modelFaceAPI.getImages({ per_page: 100 });
      setAllImages(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load all images:', err);
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: assignPage + 1,
        per_page: assignRowsPerPage,
      };
      if (assignmentSearchDebounced) {
        params.search = assignmentSearchDebounced;
      }
      const res = await modelFaceAPI.getUserAssignments(params);
      const { rows, total } = readPaginatedModelFacePayload(res);
      setAssignments(rows);
      setAssignTotal(total);
    } catch (err) {
      console.error('Failed to load assignments:', err);
      setSnackbar({ open: true, message: 'Failed to load user assignments', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [assignPage, assignRowsPerPage, assignmentSearchDebounced]);

  const loadAddonServices = useCallback(async () => {
    try {
      const res = await modelFaceAPI.getAddonServices();
      setAddonServices(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load addon services:', err);
    }
  }, []);

  const loadEnabledUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: configPage + 1,
        per_page: configRowsPerPage,
      };
      if (merchantAddonSearchDebounced) {
        params.search = merchantAddonSearchDebounced;
      }
      const res = await modelFaceAPI.getEnabledUsers(params);
      const { rows, total } = readPaginatedModelFacePayload(res);
      setEnabledUsers(rows);
      setConfigTotal(total);
    } catch (err) {
      console.error('Failed to load enabled users:', err);
      setSnackbar({ open: true, message: 'Failed to load enabled users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [configPage, configRowsPerPage, merchantAddonSearchDebounced]);

  // Debounce search input; when the query actually changes, reset to page 0 before the next load.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = merchantAddonSearchInput.trim();
      setMerchantAddonSearchDebounced((prev) => {
        if (prev !== next) {
          setConfigPage(0);
        }
        return next;
      });
    }, 350);
    return () => clearTimeout(t);
  }, [merchantAddonSearchInput]);

  // User assignments table search — debounce and reset page when the query changes.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = assignmentSearchInput.trim();
      setAssignmentSearchDebounced((prev) => {
        if (prev !== next) {
          setAssignPage(0);
        }
        return next;
      });
    }, 350);
    return () => clearTimeout(t);
  }, [assignmentSearchInput]);

  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);

  // Load each tab in isolation so updating search filters (load* callback identity) does not refetch other tabs.
  useEffect(() => {
    if (tabValue !== 0) return;
    loadEnabledUsers();
  }, [tabValue, loadEnabledUsers]);

  useEffect(() => {
    if (tabValue !== 0) return;
    loadAddonServices();
  }, [tabValue, loadAddonServices]);

  useEffect(() => {
    // First-time flow only: when opening "Configure merchant addons" and there is no prior DB config,
    // pre-check "AI Model Studio" (model_face) so the user sees it enabled by default.
    if (!configDialog || configDialogPrefilled) return;
    if (!selectedConfigUser) return;
    if (configDefaultAppliedRef.current) return;
    if (selectedAddonIds.length > 0) {
      configDefaultAppliedRef.current = true;
      return;
    }
    if (defaultModelFaceAddonId == null) return;
    setSelectedAddonIds([defaultModelFaceAddonId]);
    configDefaultAppliedRef.current = true;
  }, [
    configDialog,
    configDialogPrefilled,
    selectedConfigUser,
    selectedAddonIds.length,
    defaultModelFaceAddonId,
  ]);

  useEffect(() => {
    if (tabValue !== 1) return;
    loadImages();
  }, [tabValue, loadImages]);

  useEffect(() => {
    if (tabValue !== 2) return;
    loadAssignments();
    loadAllImages();
  }, [tabValue, loadAssignments, loadAllImages]);

  useEffect(() => {
    if (!imagePreview.open || !imagePreview.image?.id) {
      setImagePreviewUsage({
        loading: false,
        completed: null,
        total: null,
        validityDays: null,
        error: null,
      });
      return;
    }
    let cancelled = false;
    const catalogId = imagePreview.image.id;
    setImagePreviewUsage({ loading: true, completed: null, total: null, validityDays: null, error: null });
    modelFaceAPI
      .getImageUsageSummary(catalogId)
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data || {};
        setImagePreviewUsage({
          loading: false,
          completed: Number(d.generations_completed) || 0,
          total: Number(d.generations_total) || 0,
          validityDays: d.validity_days != null ? d.validity_days : null,
          error: null,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setImagePreviewUsage({
            loading: false,
            completed: null,
            total: null,
            validityDays: null,
            error: 'Could not load generation stats',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [imagePreview.open, imagePreview.image?.id]);

  const filteredAndSortedImages = useMemo(() => {
    let data = [...images];

    if (imageSearch.trim()) {
      const q = imageSearch.trim().toLowerCase();
      data = data.filter(img =>
        img.display_name?.toLowerCase().includes(q) ||
        img.name?.toLowerCase().includes(q) ||
        img.description?.toLowerCase().includes(q)
      );
    }

    if (imageGenderFilter) {
      data = data.filter(img => String(img.gender_option_id || '') === String(imageGenderFilter));
    }

    if (imageEthnicityFilter) {
      data = data.filter(img => String(img.ethnicity_id || '') === String(imageEthnicityFilter));
    }

    if (imageActiveFilter) {
      const active = imageActiveFilter === 'active';
      data = data.filter(img => !!img.is_active === active);
    }

    if (imageAvailabilityFilter === 'booked') {
      data = data.filter((img) => Number(img.availability_status) === 2);
    } else if (imageAvailabilityFilter === 'available') {
      data = data.filter((img) => Number(img.availability_status) !== 2);
    }

    data.sort((a, b) => {
      const dir = imageSortDir === 'asc' ? 1 : -1;
      const av = (a[imageSortBy] || '').toString().toLowerCase();
      const bv = (b[imageSortBy] || '').toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

    return data;
  }, [images, imageSearch, imageGenderFilter, imageEthnicityFilter, imageActiveFilter, imageAvailabilityFilter, imageSortBy, imageSortDir]);

  const assignSelectedModels = useMemo(() => {
    const ids = assignForm.model_face_image_ids || [];
    return ids.map((id) => allImages.find((img) => img.id === id)).filter(Boolean);
  }, [assignForm.model_face_image_ids, allImages]);

  const assignTotalTokens = useMemo(
    () => assignSelectedModels.reduce((sum, m) => sum + (Number(m.credit_cost) || 0), 0),
    [assignSelectedModels],
  );

  const assignInsufficient =
    assignBalance !== null && assignTotalTokens > assignBalance;

  const assignmentsByUser = useMemo(() => {
    // Backend API /model-face/user-assignments now returns data grouped by merchant (User).
    // This resolves pagination inconsistencies where one merchant with many portraits would consume multiple backend 'records'.
    return assignments || [];
  }, [assignments]);

  // ──────────────────────────────────────────────
  // USER SEARCH (debounced)
  // ──────────────────────────────────────────────

  const searchUsers = async (query, setResults, setLoadingState) => {
    if (!query || query.length < 2) { setResults([]); return; }
    setLoadingState(true);
    try {
      const res = await modelFaceAPI.searchUsers(query);
      const p = res?.data;
      setResults(Array.isArray(p?.data) ? p.data : []);
    } catch (err) {
      console.error('User search failed:', err);
    } finally {
      setLoadingState(false);
    }
  };

  // ──────────────────────────────────────────────
  // IMAGE HANDLERS
  // ──────────────────────────────────────────────

  const resetImageForm = () => {
    setImageForm({
      name: '', display_name: '', description: '',
      gender_option_id: '', ethnicity_id: '', is_active: true, sort_order: 0,
      credit_cost: '',
      validity_days: '',
    });
    setImageFormErrors({ ...INITIAL_IMAGE_FORM_ERRORS });
    setEditingImage(null);
    setSelectedFile(null);
    if (previewUrl && !previewUrl.startsWith('http')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  /** Clears one field’s inline error when the user edits that field. */
  const clearImageFormError = (key) => {
    setImageFormErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  };

  const handleImageEdit = (img) => {
    setImageFormErrors({ ...INITIAL_IMAGE_FORM_ERRORS });
    setEditingImage(img);
    setImageForm({
      name: img.name,
      display_name: img.display_name,
      description: img.description || '',
      gender_option_id: img.gender_option_id || '',
      ethnicity_id: img.ethnicity_id || '',
      is_active: img.is_active,
      sort_order: img.sort_order || 0,
      credit_cost: img.credit_cost != null ? String(img.credit_cost) : '',
      // API null = lifetime; show 0 so it matches “0 for lifetime” and saves reliably.
      validity_days: img.validity_days != null && img.validity_days !== '' ? String(img.validity_days) : '0',
    });
    setPreviewUrl(getModelFaceImageUrl(img));
    setImageDialog(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      clearImageFormError('portrait');
    }
  };

  const handleImageSave = async () => {
    // Enforce required fields for create/edit; description stays optional. Portrait file is required only on create.
    const trimmedName = String(imageForm.name || '').trim();
    const trimmedDisplay = String(imageForm.display_name || '').trim();
    const errors = { ...INITIAL_IMAGE_FORM_ERRORS };

    if (!trimmedName) errors.name = 'Reference name is required.';
    if (!trimmedDisplay) errors.display_name = 'Display name is required.';
    if (!imageForm.gender_option_id) errors.gender_option_id = 'Select a gender.';
    if (!imageForm.ethnicity_id) errors.ethnicity_id = 'Select an ethnicity.';

    const sortN = Number(imageForm.sort_order);
    if (!Number.isInteger(sortN) || sortN < 0) {
      errors.sort_order = 'Enter a whole number ≥ 0.';
    }

    const ccRaw = String(imageForm.credit_cost ?? '').trim();
    const ccNum = ccRaw === '' ? NaN : Number(ccRaw);
    if (ccRaw === '' || !Number.isFinite(ccNum) || ccNum < 0 || !Number.isInteger(ccNum)) {
      errors.credit_cost = 'Enter a whole number ≥ 0.';
    }

    // Empty field = lifetime, same as 0 (backend stores NULL on the catalog row).
    const vdRaw = String(imageForm.validity_days ?? '').trim();
    const vdNum = vdRaw === '' ? 0 : Number(vdRaw);
    if (!Number.isFinite(vdNum) || vdNum < 0 || !Number.isInteger(vdNum)) {
      errors.validity_days = 'Enter a whole number ≥ 0, or leave blank for lifetime access.';
    }

    if (!editingImage && !selectedFile) {
      errors.portrait = 'Upload a portrait image (JPG/PNG).';
    }

    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      setImageFormErrors(errors);
      return;
    }

    setImageFormErrors({ ...INITIAL_IMAGE_FORM_ERRORS });

    if (imageSaveLoading) return;

    setImageSaveLoading(true);
    try {
      const formData = new FormData();
      Object.keys(imageForm).forEach((key) => {
        let v = imageForm[key];
        // Always send a numeric string so multipart never drops “0” / empty lifetime by accident.
        if (key === 'validity_days') {
          const t = String(v ?? '').trim();
          v = t === '' ? '0' : t;
        }
        formData.append(key, v);
      });
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      if (editingImage) {
        await modelFaceAPI.updateImage(editingImage.id, formData);
        setSnackbar({ open: true, message: 'Image updated', severity: 'success' });
      } else {
        await modelFaceAPI.createImage(formData);
        setSnackbar({ open: true, message: 'Image created', severity: 'success' });
      }
      setImageDialog(false);
      resetImageForm();
      loadImages();
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to save image';
      // Server rejects duplicate reference/display among active rows; show under the matching field.
      const isRefDup = /reference name/i.test(errMsg);
      const isDisplayDup = /display name/i.test(errMsg);
      if (isRefDup || isDisplayDup) {
        setImageFormErrors((prev) => ({
          ...prev,
          ...(isRefDup ? { name: errMsg } : {}),
          ...(isDisplayDup ? { display_name: errMsg } : {}),
        }));
      } else {
        setSnackbar({ open: true, message: errMsg, severity: 'error' });
      }
    } finally {
      setImageSaveLoading(false);
    }
  };

  const handleImageDelete = (img) => {
    setDeleteConfirm({ open: true, image: img, loading: false });
  };

  const handleConfirmDelete = async () => {
    const imageId = deleteConfirm.image?.id;
    if (!imageId) return;
    
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    try {
      await modelFaceAPI.deleteImage(imageId);
      setSnackbar({ open: true, message: 'Catalog model removed successfully', severity: 'success' });
      setDeleteConfirm({ open: false, image: null, loading: false });
      loadImages();
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to delete image';
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleImageToggleActive = async (img) => {
    try {
      const formData = new FormData();
      formData.append('is_active', (!img.is_active).toString());
      await modelFaceAPI.updateImage(img.id, formData);
      setSnackbar({ open: true, message: `Image ${!img.is_active ? 'activated' : 'deactivated'}`, severity: 'success' });
      loadImages();
    } catch (err) {
      // Backend rejects activation when reference, display, gender, ethnicity, or portrait are incomplete.
      const msg = err.response?.data?.error || 'Failed to update image status';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleBulkUploadFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setBulkUploadFile(file);
  };
  
  const handleBulkUploadSubmit = async () => {
    if (!bulkUploadFile) {
      setSnackbar({ open: true, message: 'Please select an Excel file', severity: 'error' });
      return;
    }
    setBulkUploadLoading(true);
    setBulkUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);
      const res = await modelFaceAPI.bulkUploadExcel(formData);
      setBulkUploadResult({ type: 'success', message: res.data.message, errors: res.data.errors });
      loadImages(); // refresh table
    } catch (err) {
      setBulkUploadResult({ type: 'error', message: err.response?.data?.error || 'Failed to upload' });
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const resp = await modelFaceAPI.downloadExcelTemplate();
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'model_catalog_bulk_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to download template', severity: 'error' });
    }
  };

  // ──────────────────────────────────────────────
  // ASSIGNMENT HANDLERS
  // ──────────────────────────────────────────────

  /** Opens confirmation dialog and loads fresh merchant token balance. */
  const handleAssignReview = async () => {
    const userId = assignForm.user_id?.id || assignForm.user_id;
    if (!userId || !assignForm.model_face_image_ids || assignForm.model_face_image_ids.length === 0) {
      setSnackbar({ open: true, message: 'Select a user and at least one catalog model', severity: 'error' });
      return;
    }
    setAssignBalanceLoading(true);
    setAssignConfirmOpen(true);
    setAssignBalance(null);
    try {
      const res = await modelFaceAPI.getUserCreditsBalance(userId);
      setAssignBalance(res.data?.data?.credits_balance ?? null);
    } catch (err) {
      console.error('Failed to load merchant token balance:', err);
      setSnackbar({ open: true, message: 'Could not load merchant token balance', severity: 'warning' });
    } finally {
      setAssignBalanceLoading(false);
    }
  };

  /** One request: assigns all selected models and deducts total tokens from the merchant. */
  const handleAssignConfirm = async () => {
    const userId = assignForm.user_id?.id || assignForm.user_id;
    if (!userId || !assignForm.model_face_image_ids?.length) return;
    if (assignInsufficient) return;
    setAssignSubmitting(true);
    try {
      const resp = await modelFaceAPI.createUserAssignment({
        user_id: userId,
        model_face_image_ids: assignForm.model_face_image_ids,
      });
      const charged = resp.data?.total_credits_charged;
      const after = resp.data?.credits_balance_after;
      let msg = 'User assigned successfully';
      if (charged != null && charged > 0) {
        msg = `Deducted ${charged} tokens from the merchant. Balance after: ${after ?? '—'}.`;
      } else if (charged === 0) {
        msg = 'Assignment completed (no tokens charged for selected models).';
      }
      setSnackbar({ open: true, message: msg, severity: 'success' });
      setAssignConfirmOpen(false);
      setAssignDialog(false);
      setAssignForm({ user_id: null, model_face_image_ids: [] });
      setAssignBalance(null);
      loadAssignments();
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to assign user';
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    } finally {
      setAssignSubmitting(false);
    }
  };

  /** Opens styled confirm dialog; actual delete runs in handleRevokeAssignmentConfirm. */
  const openRevokeAssignmentConfirm = (assignment) => {
    setRevokeAssignmentConfirm({
      open: true,
      assignment,
      merchantName: assignmentPreview.row?.user?.name || '',
      loading: false,
    });
  };

  const handleRevokeAssignmentCancel = () => {
    if (!revokeAssignmentConfirm.loading) {
      setRevokeAssignmentConfirm({
        open: false,
        assignment: null,
        merchantName: '',
        loading: false,
      });
    }
  };

  const handleRevokeAssignmentConfirm = async () => {
    const assignmentId = revokeAssignmentConfirm.assignment?.id;
    if (!assignmentId) return;
    setRevokeAssignmentConfirm((prev) => ({ ...prev, loading: true }));
    try {
      await modelFaceAPI.deleteUserAssignment(assignmentId);
      setSnackbar({ open: true, message: 'Assignment removed', severity: 'success' });
      // Keep faces modal in sync after successful revoke (no optimistic remove before confirm).
      setAssignmentPreview((prev) =>
        prev.row && prev.open
          ? {
              ...prev,
              row: {
                ...prev.row,
                assignments: prev.row.assignments.filter((x) => x.id !== assignmentId),
              },
            }
          : prev,
      );
      setRevokeAssignmentConfirm({
        open: false,
        assignment: null,
        merchantName: '',
        loading: false,
      });
      loadAssignments();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove assignment', severity: 'error' });
      setRevokeAssignmentConfirm((prev) => ({ ...prev, loading: false }));
    }
  };

  // ──────────────────────────────────────────────
  // CONFIG HANDLERS
  // ──────────────────────────────────────────────

  const handleEnableUser = async () => {
    const userId = selectedConfigUser?.id;
    if (!userId) {
      setSnackbar({ open: true, message: 'Select a user', severity: 'error' });
      return;
    }
    if (!configDialogPrefilled && selectedAddonIds.length === 0) {
      setSnackbar({ open: true, message: 'Select at least one service', severity: 'error' });
      return;
    }
    try {
      if (configDialogPrefilled) {
        // Edit from row: persist full checkbox state (enable + disable) for every known service.
        await Promise.all(
          (addonServices || []).map((svc) =>
            modelFaceAPI.updateUserConfig(userId, {
              addon_service_id: svc.id,
              is_enabled: selectedAddonIds.includes(svc.id),
            }),
          ),
        );
      } else {
        await Promise.all(
          selectedAddonIds.map((addonId) =>
            modelFaceAPI.updateUserConfig(userId, { addon_service_id: addonId, is_enabled: true }),
          ),
        );
      }

      setSnackbar({
        open: true,
        message: configDialogPrefilled ? 'Addon services updated' : 'Addon services enabled for user',
        severity: 'success',
      });
      setConfigDialog(false);
      setConfigDialogPrefilled(false);
      setSelectedConfigUser(null);
      setSelectedAddonIds([]);
      setConfigUserSearch([]);
      loadEnabledUsers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save addon services', severity: 'error' });
    }
  };

  const fetchAuditLogs = useCallback(async (catalogId, pageIdx) => {
    setAuditLogLoading(true);
    try {
      const res = await modelFaceAPI.getAuditLogs({
        model_catalog_id: catalogId,
        page: pageIdx + 1,
        per_page: auditLogPerPage,
      });
      setAuditLogRows(res.data?.data ?? []);
      setAuditLogTotal(res.data?.total ?? 0);
    } catch (err) {
      console.error(err);
      setAuditLogRows([]);
      setAuditLogTotal(0);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to load activity log',
        severity: 'error',
      });
    } finally {
      setAuditLogLoading(false);
    }
  }, [auditLogPerPage]);

  const openAuditLogs = (img) => {
    if (!img?.id) return;
    setAuditLogImage(img);
    setAuditLogOpen(true);
    setAuditLogPage(0);
    fetchAuditLogs(img.id, 0);
  };

  // ──────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        AI Model Face Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage the model catalog (token pricing and availability), merchant assignments, and per-user generation toggles.
      </Typography>

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<StarIcon />} label="Merchant Addons" sx={{ display: 'none' }} />
          <Tab icon={<PhotoIcon />} label="Model catalog" />
          <Tab icon={<PeopleIcon />} label="User Assignments" />
        </Tabs>

        {/* Merchant Addons */}
        <TabPanel value={tabValue} index={0}>
          <UserConfigPanel
            loading={loading}
            enabledUsers={enabledUsers}
            configPage={configPage}
            setConfigPage={setConfigPage}
            configRowsPerPage={configRowsPerPage}
            setConfigRowsPerPage={setConfigRowsPerPage}
            configTotal={configTotal}
            merchantAddonSearchInput={merchantAddonSearchInput}
            setMerchantAddonSearchInput={setMerchantAddonSearchInput}
            merchantAddonSearchActive={!!merchantAddonSearchDebounced}
            onOpenEnableDialog={(row) => {
              setConfigDialogPrefilled(!!row);
              configDefaultAppliedRef.current = false;
              if (row) {
                const u = {
                  id: row.user_id,
                  name: row.user?.name,
                  email: row.user?.email,
                  company_name: row.user?.company_name,
                };
                setSelectedConfigUser(u);
                setSelectedAddonIds(
                  (row.enabled_services || []).map((s) => s.addon_service_id).filter(Boolean),
                );
                setConfigUserSearch([u]);
              } else {
                setSelectedConfigUser(null);
                setSelectedAddonIds([]);
                setConfigUserSearch([]);
              }
              setConfigDialog(true);
            }}
          />
        </TabPanel>

        {/* Model catalog (portraits) */}
        <TabPanel value={tabValue} index={1}>
          <FaceImagePanel
            images={filteredAndSortedImages}
            loading={loading}
            imageViewMode={imageViewMode}
            setImageViewMode={setImageViewMode}
            imageSearch={imageSearch}
            setImageSearch={setImageSearch}
            imageGenderFilter={imageGenderFilter}
            setImageGenderFilter={setImageGenderFilter}
            imageEthnicityFilter={imageEthnicityFilter}
            setImageEthnicityFilter={setImageEthnicityFilter}
            imageActiveFilter={imageActiveFilter}
            setImageActiveFilter={setImageActiveFilter}
            imageAvailabilityFilter={imageAvailabilityFilter}
            setImageAvailabilityFilter={setImageAvailabilityFilter}
            imageSortBy={imageSortBy}
            setImageSortBy={setImageSortBy}
            imageSortDir={imageSortDir}
            setImageSortDir={setImageSortDir}
            genderOptions={genderOptions}
            ethnicities={ethnicities}
            imagePage={imagePage}
            setImagePage={setImagePage}
            imageRowsPerPage={imageRowsPerPage}
            setImageRowsPerPage={setImageRowsPerPage}
            imageTotal={imageTotal}
            onAddImage={() => {
              resetImageForm();
              setImageDialog(true);
            }}
            onBulkUpload={() => {
              setBulkUploadFile(null);
              setBulkUploadResult(null);
              setBulkUploadDialog(true);
            }}
            onEditImage={handleImageEdit}
            onDeleteImage={handleImageDelete}
            onToggleActive={handleImageToggleActive}
            onPreviewImage={(img) => setImagePreview({ open: true, image: img })}
            onViewAuditLogs={openAuditLogs}
          />
	        </TabPanel>

	        {/* User Assignments */}
	        <TabPanel value={tabValue} index={2}>
	          <UserAssignmentsPanel
	            loading={loading}
	            assignmentsByUser={assignmentsByUser}
	            assignPage={assignPage}
	            setAssignPage={setAssignPage}
	            assignRowsPerPage={assignRowsPerPage}
	            setAssignRowsPerPage={setAssignRowsPerPage}
	            assignTotal={assignTotal}
	            assignmentSearchInput={assignmentSearchInput}
	            setAssignmentSearchInput={setAssignmentSearchInput}
	            assignmentSearchActive={!!assignmentSearchDebounced}
	            onOpenAssignDialog={() => {
	              setAssignForm({ user_id: null, model_face_image_ids: [] });
	              setAssignDialog(true);
	            }}
	            onOpenFacesModal={(row) => setAssignmentPreview({ open: true, row })}
	          />
	        </TabPanel>


      </Paper>

      {/* ════════════════════════════════════════════
          DIALOGS
         ════════════════════════════════════════════ */}

      {/* Image Create/Edit Dialog — must not nest other Dialogs inside (breaks MUI / bulk upload). */}
      <Dialog
        open={imageDialog}
        onClose={() => {
          if (imageSaveLoading) return;
          setImageFormErrors({ ...INITIAL_IMAGE_FORM_ERRORS });
          setImageDialog(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingImage ? 'Edit catalog model' : 'Add catalog model'}</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50/50' }}>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Basic Info Section */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" fontWeight="bold">Basic Information</Typography>
              <Paper variant="outlined" sx={{ p: 2.5, mt: 1, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Reference Name"
                      required
                      placeholder="e.g. young_asian_female_01"
                      value={imageForm.name}
                      onChange={(e) => {
                        setImageForm({ ...imageForm, name: e.target.value });
                        clearImageFormError('name');
                      }}
                      error={Boolean(imageFormErrors.name)}
                      helperText={imageFormErrors.name || undefined}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Display Name"
                      required
                      placeholder="e.g. Mei Ling"
                      value={imageForm.display_name}
                      onChange={(e) => {
                        setImageForm({ ...imageForm, display_name: e.target.value });
                        clearImageFormError('display_name');
                      }}
                      error={Boolean(imageFormErrors.display_name)}
                      helperText={imageFormErrors.display_name || undefined}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Description" multiline rows={2}
                      placeholder="Enter a brief description..."
                      value={imageForm.description}
                      onChange={(e) => setImageForm({ ...imageForm, description: e.target.value })}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Visuals and Attributes Section */}
            <Grid item xs={12} sm={5}>
              <Typography variant="overline" color="text.secondary" fontWeight="bold">
                Image Asset <Typography component="span" color="error.main">*</Typography>
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mt: 1,
                  borderRadius: 2,
                  height: 'calc(100% - 24px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  borderColor: imageFormErrors.portrait ? 'error.main' : 'divider',
                }}
              >
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: 140, 
                    height: 140, 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {previewUrl ? (
                    <Box component="img" src={previewUrl} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <PhotoIcon sx={{ fontSize: 48, color: 'divider' }} />
                  )}
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    component="label"
                    variant="contained"
                    size="small"
                    startIcon={<PhotoIcon />}
                    disabled={imageSaveLoading}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    {editingImage ? 'Update Portrait' : 'Upload Portrait'}
                    <input type="file" hidden accept="image/*" onChange={handleFileChange} disabled={imageSaveLoading} />
                  </Button>
                  {/* No client-side file size check; backend does not cap uploads at 2MB here either. */}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    JPG or PNG recommended
                    <br />
                    Suggested dimensions: 512×512
                  </Typography>
                  {imageFormErrors.portrait ? (
                    <FormHelperText error sx={{ justifyContent: 'center', mx: 0, textAlign: 'center' }}>
                      {imageFormErrors.portrait}
                    </FormHelperText>
                  ) : null}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={7}>
              <Typography variant="overline" color="text.secondary" fontWeight="bold">Attributes & Settings</Typography>
              <Paper variant="outlined" sx={{ p: 2.5, mt: 1, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <FormControl fullWidth required size="small" error={Boolean(imageFormErrors.gender_option_id)}>
                  <InputLabel>Gender Primary</InputLabel>
                  <Select
                    value={imageForm.gender_option_id}
                    disabled={isBookedReadOnly}
                    onChange={(e) => {
                      setImageForm({ ...imageForm, gender_option_id: e.target.value });
                      clearImageFormError('gender_option_id');
                    }}
                    label="Gender Primary"
                    sx={{ borderRadius: 2 }}
                  >
                    {/* Placeholder row: value '' is invalid at submit until a real option is chosen. */}
                    <MenuItem value="" disabled><em>Select gender</em></MenuItem>
                    {genderOptions.map(g => (
                      <MenuItem key={g.id} value={g.id}>{g.display_name}</MenuItem>
                    ))}
                  </Select>
                  {imageFormErrors.gender_option_id ? (
                    <FormHelperText>{imageFormErrors.gender_option_id}</FormHelperText>
                  ) : null}
                </FormControl>

                <FormControl fullWidth required size="small" error={Boolean(imageFormErrors.ethnicity_id)}>
                  <InputLabel>Ethnicity</InputLabel>
                  <Select
                    value={imageForm.ethnicity_id}
                    disabled={isBookedReadOnly}
                    onChange={(e) => {
                      setImageForm({ ...imageForm, ethnicity_id: e.target.value });
                      clearImageFormError('ethnicity_id');
                    }}
                    label="Ethnicity"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="" disabled><em>Select ethnicity</em></MenuItem>
                    {ethnicities.map(e => (
                      <MenuItem key={e.id} value={e.id}>{e.display_name}</MenuItem>
                    ))}
                  </Select>
                  {imageFormErrors.ethnicity_id ? (
                    <FormHelperText>{imageFormErrors.ethnicity_id}</FormHelperText>
                  ) : null}
                </FormControl>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Order Index"
                      size="small"
                      value={imageForm.sort_order}
                      disabled={isBookedReadOnly}
                      onChange={(e) => {
                        setImageForm({ ...imageForm, sort_order: parseInt(e.target.value, 10) || 0 });
                        clearImageFormError('sort_order');
                      }}
                      error={Boolean(imageFormErrors.sort_order)}
                      helperText={imageFormErrors.sort_order || undefined}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', pl: 1 }}>
                      <FormControlLabel
                        control={<Switch checked={imageForm.is_active}
                          onChange={(e) => setImageForm({ ...imageForm, is_active: e.target.checked })}
                          color="primary" />}
                        label={<Typography variant="body2" fontWeight="bold">Live Status</Typography>} />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="overline" color="text.secondary" fontWeight="bold">
                      Tokens (booking cost)
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Token cost"
                      type="number"
                      size="small"
                      value={imageForm.credit_cost}
                      disabled={isBookedReadOnly}
                      onChange={(e) => {
                        setImageForm({ ...imageForm, credit_cost: e.target.value });
                        clearImageFormError('credit_cost');
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      inputProps={{ min: 0, step: 1 }}
                      error={Boolean(imageFormErrors.credit_cost)}
                      helperText={
                        imageFormErrors.credit_cost ||
                        'Tokens charged when a merchant books this model globally.'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Access validity (days)"
                      type="number"
                      size="small"
                      value={imageForm.validity_days}
                      disabled={isBookedReadOnly}
                      onChange={(e) => {
                        setImageForm({ ...imageForm, validity_days: e.target.value });
                        clearImageFormError('validity_days');
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      inputProps={{ min: 0, step: 1 }}
                      error={Boolean(imageFormErrors.validity_days)}
                      helperText={
                        imageFormErrors.validity_days ||
                        'Days of access after booking. Use 0 or leave empty for lifetime (no expiry).'
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={() => {
              if (imageSaveLoading) return;
              setImageFormErrors({ ...INITIAL_IMAGE_FORM_ERRORS });
              setImageDialog(false);
            }}
            disabled={imageSaveLoading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImageSave}
            disabled={imageSaveLoading}
            sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600, minWidth: 160 }}
            startIcon={
              imageSaveLoading ? (
                <CircularProgress size={18} color="inherit" aria-hidden />
              ) : null
            }
          >
            {imageSaveLoading
              ? editingImage
                ? 'Saving…'
                : 'Creating…'
              : editingImage
                ? 'Save Changes'
                : 'Create Image'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel bulk upload only (sibling dialog — was incorrectly nested inside image dialog). */}
      <Dialog open={bulkUploadDialog} onClose={() => setBulkUploadDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk upload (Excel only)</DialogTitle>
        <DialogContent dividers>
          {/* Short copy; the downloaded template’s Instructions sheet has full column detail. */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Download the template — fill the <strong>Models</strong> sheet; use <strong>Instructions</strong> if you need more detail.
          </Typography>
          <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.25 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              One row per model. You need <strong>name</strong> and <strong>display name</strong>.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <strong>Gender</strong> and <strong>ethnicity</strong> must match your Admin lists (case does not matter).
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Add the <strong>photo in column H</strong> for that row, or leave H blank and upload the photo later in Edit.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Optional: <strong>I</strong> = access days after booking, <strong>J</strong> = sort order in the list.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Advanced:</strong> use <code>image_path</code> only for an image already stored on the server — not a folder path from your computer.
            </Typography>
          </Box>

          <Button variant="outlined" size="small" onClick={handleDownloadTemplate} sx={{ mb: 2 }}>
            Download template with examples
          </Button>
          <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
            <Button component="label" variant="contained" disabled={bulkUploadLoading}>
              Select Excel file (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleBulkUploadFileChange}
              />
            </Button>
            {bulkUploadFile && (
              <Typography variant="body2" sx={{ mt: 2, color: 'success.main', fontWeight: 'bold' }}>
                Selected: {bulkUploadFile.name}
              </Typography>
            )}
          </Box>
          {bulkUploadResult && (
            <Alert severity={bulkUploadResult.type} sx={{ mt: 3 }}>
              {bulkUploadResult.message}
              {bulkUploadResult.errors?.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 20, marginTop: 8 }}>
                  {bulkUploadResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadDialog(false)}>Close</Button>
          <Button onClick={handleBulkUploadSubmit} variant="contained" disabled={!bulkUploadFile || bulkUploadLoading}>
            {bulkUploadLoading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <AssignUserModal
        open={assignDialog}
        onClose={() => {
          setAssignDialog(false);
          setAssignConfirmOpen(false);
          setAssignBalance(null);
        }}
        assignForm={assignForm}
        setAssignForm={setAssignForm}
        userSearchResults={userSearchResults}
        userSearchLoading={userSearchLoading}
        searchUsers={searchUsers}
        setUserSearchResults={setUserSearchResults}
        setUserSearchLoading={setUserSearchLoading}
        allImages={allImages}
        onReview={handleAssignReview}
      />

      {/* Confirm balance, line items, and total before deducting merchant tokens. */}
      <Dialog
        open={assignConfirmOpen}
        onClose={() => !assignSubmitting && setAssignConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm assignment &amp; tokens</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50/30' }}>
          {assignBalanceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The merchant&apos;s account will be charged the same token total as self-service booking (sum of each model&apos;s token cost).
              </Typography>
              <Typography variant="overline" color="text.secondary" fontWeight="bold">
                Merchant
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {assignForm.user_id?.name || '—'} ({assignForm.user_id?.email || '—'})
              </Typography>
              <Typography variant="overline" color="text.secondary" fontWeight="bold">
                Available tokens
              </Typography>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                {assignBalance !== null ? assignBalance : '—'}
              </Typography>
              {!assignBalanceLoading && assignBalance === null && assignTotalTokens > 0 && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  Balance could not be loaded. The server will still enforce sufficient tokens.
                </Alert>
              )}
              <Typography variant="overline" color="text.secondary" fontWeight="bold">
                Selected models
              </Typography>
              <Table size="small" sx={{ mb: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignSelectedModels.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.display_name}</TableCell>
                      <TableCell align="right">{Number(m.credit_cost) || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Total to deduct
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {formatTokenAmount(assignTotalTokens)}
                </Typography>
              </Box>
              {assignInsufficient && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  Not enough tokens. This merchant needs {assignTotalTokens - assignBalance} more tokens.
                </Alert>
              )}
              {assignTotalTokens === 0 && assignSelectedModels.length > 0 && (
                <Alert severity="info" sx={{ borderRadius: 2, mt: assignInsufficient ? 1 : 0 }}>
                  Selected models have zero token cost — no deduction will occur.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={() => !assignSubmitting && setAssignConfirmOpen(false)}
            disabled={assignSubmitting}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignConfirm}
            disabled={
              assignSubmitting ||
              assignBalanceLoading ||
              assignInsufficient ||
              assignSelectedModels.length === 0
            }
            sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
          >
            {assignSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Deduct tokens & assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enable / edit merchant addon services */}
      <Dialog
        open={configDialog}
        onClose={() => {
          setConfigDialog(false);
          setConfigDialogPrefilled(false);
          setConfigUserSearch([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            py: 2.5,
            px: 3,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'primary.contrastText',
          }}
        >
          {configDialogPrefilled ? 'Edit merchant addon services' : 'Configure merchant addons'}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {configDialogPrefilled
                ? 'Update which services this merchant can use. Changes apply on save.'
                : 'Search for a merchant and choose which addon services to enable.'}
            </Typography>
            <Autocomplete
              options={configUserSearch}
              value={selectedConfigUser}
              isOptionEqualToValue={(a, b) => !!a && !!b && String(a.id) === String(b.id)}
              getOptionLabel={formatMerchantAutocompleteLabel}
              loading={configUserSearchLoading}
              disabled={configDialogPrefilled}
              onInputChange={(e, val) => {
                if (!configDialogPrefilled) searchUsers(val, setConfigUserSearch, setConfigUserSearchLoading);
              }}
              onChange={(e, val) => setSelectedConfigUser(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Merchant"
                  placeholder="Name, email, mobile, or WhatsApp…"
                  sx={{ bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {configUserSearchLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 1.5 }}>
              Addon services
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <FormGroup sx={{ px: 0.5 }}>
                {addonServices.map((service) => (
                  <FormControlLabel
                    key={service.id}
                    control={
                      <Checkbox
                        checked={selectedAddonIds.includes(service.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedAddonIds([...selectedAddonIds, service.id]);
                          else setSelectedAddonIds(selectedAddonIds.filter((id) => id !== service.id));
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {service.name}
                        </Typography>
                        {service.description ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {service.description}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                    sx={{ mb: 0.5, alignItems: 'flex-start', ml: 0 }}
                  />
                ))}
              </FormGroup>
            </Paper>
          </Box>

          {!configDialogPrefilled && selectedAddonIds.length === 0 && (
            <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
              Select at least one service to enable for this merchant.
            </Alert>
          )}
          {configDialogPrefilled && selectedAddonIds.length === 0 && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
              No services selected — saving will disable all addons for this merchant.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, bgcolor: 'grey.100', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => {
              setConfigDialog(false);
              setConfigDialogPrefilled(false);
              setConfigUserSearch([]);
              configDefaultAppliedRef.current = false;
            }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleEnableUser}
            disabled={
              !selectedConfigUser || (!configDialogPrefilled && selectedAddonIds.length === 0)
            }
            sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
          >
            {configDialogPrefilled ? 'Save changes' : 'Enable services'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity log for one catalog model (ai_model_addon_logs) */}
      <Dialog
        open={auditLogOpen}
        onClose={() => {
          setAuditLogOpen(false);
          setAuditLogImage(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            py: 2.5,
            px: 3,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'primary.contrastText',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HistoryIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                Activity history
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                Audit trail for this catalog portrait
              </Typography>
              {auditLogImage && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    mt: 1,
                    fontWeight: 600,
                    opacity: 0.95,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={auditLogImage.display_name}
                >
                  {auditLogImage.display_name}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setAuditLogOpen(false);
              setAuditLogImage(null);
            }}
            sx={{ color: 'inherit', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
            aria-label="Close activity history"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
          {auditLogLoading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress size={36} />
            </Box>
          ) : auditLogRows.length === 0 ? (
            <Box
              sx={{
                py: 8,
                px: 3,
                textAlign: 'center',
                bgcolor: 'background.paper',
              }}
            >
              <HistoryIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1.5 }} />
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                No events yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
                Changes, assignments, and purchases for this model will appear here.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                mx: 2,
                my: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={auditLogHeadCellSx}>Date &amp; time</TableCell>
                    <TableCell sx={auditLogHeadCellSx}>Event type</TableCell>
                    <TableCell sx={{ ...auditLogHeadCellSx, minWidth: 200 }}>Description</TableCell>
                    <TableCell align="right" sx={auditLogHeadCellSx}>
                      Tokens
                    </TableCell>
                    <TableCell sx={{ ...auditLogHeadCellSx, minWidth: 160 }}>Performed by</TableCell>
                    <TableCell sx={{ ...auditLogHeadCellSx, minWidth: 160 }}>Affected user</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLogRows.map((row) => (
                    <TableRow key={row.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', py: 1.5 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                          {/* Audit log time: DD/MM/YYYY + 12h in IST */}
                          {row.created_at
                            ? new Date(row.created_at).toLocaleString('en-GB', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true,
                                timeZone: 'Asia/Kolkata',
                              })
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                        <Chip
                          label={AI_MODEL_ACTION_LABELS[row.action_type] || row.action_type}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontWeight: 600, borderRadius: 1.5, height: 26 }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280, verticalAlign: 'top', py: 1.5 }}>
                        <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                          {row.summary || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'top', py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {row.credits_amount != null ? formatTokenAmount(row.credits_amount) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, verticalAlign: 'top', py: 1.5 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                          {row.actor?.label || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, verticalAlign: 'top', py: 1.5 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                          {row.subject?.label || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <TablePagination
            component="div"
            count={auditLogTotal}
            page={auditLogPage}
            onPageChange={(e, p) => {
              setAuditLogPage(p);
              if (auditLogImage?.id) fetchAuditLogs(auditLogImage.id, p);
            }}
            rowsPerPage={auditLogPerPage}
            rowsPerPageOptions={[auditLogPerPage]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
          />
          <Button
            variant="contained"
            onClick={() => setAuditLogOpen(false)}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3, fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreview.open}
        onClose={() => setImagePreview({ open: false, image: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Image Detail</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {imagePreview.image && (
              <Tooltip title="Activity log">
                <IconButton size="small" onClick={() => openAuditLogs(imagePreview.image)} color="primary">
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setImagePreview({ open: false, image: null })}>
              <DisabledIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {imagePreview.image && (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box
                  component="img"
                  src={getModelFaceImageUrl(imagePreview.image)}
                  alt={imagePreview.image.display_name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Paper>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="bold">
                      {imagePreview.image.display_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                       Internal ID: {imagePreview.image.name}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {imagePreview.image.description || 'No description provided for this catalog model.'}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    <Chip label={imagePreview.image.gender_option?.display_name || 'No Gender'} size="small" sx={{ borderRadius: 1.5, bgcolor: 'primary.lighter', color: 'primary.dark', fontWeight: 600 }} />
                    {imagePreview.image.ethnicity && (
                      <Chip label={imagePreview.image.ethnicity.display_name} size="small" sx={{ borderRadius: 1.5, bgcolor: 'grey.100', fontWeight: 500 }} />
                    )}
                    <Chip
                      icon={<PremiumIcon style={{ fontSize: 16 }} />}
                      label={imagePreview.image.availability_status === 2 ? 'Booked' : 'Available'}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        fontWeight: 'bold',
                        ...(imagePreview.image.availability_status === 2
                          ? { bgcolor: '#FFF8E1', color: '#B8860B', border: '1px solid #FFE082' }
                          : { variant: 'outlined' }),
                      }}
                    />
                </Box>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: 'grey.50/50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">TOKEN COST</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {imagePreview.image.credit_cost != null ? formatTokenAmount(imagePreview.image.credit_cost) : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">STATUS</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {imagePreview.image.availability_label || (imagePreview.image.availability_status === 2 ? 'booked' : 'available')}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">USAGE</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PeopleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight="bold">
                          {imagePreview.image.purchase_count || 0} Users
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">
                        GENERATIONS
                      </Typography>
                      {imagePreviewUsage.loading ? (
                        <CircularProgress size={20} sx={{ mt: 0.5 }} />
                      ) : imagePreviewUsage.error ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {imagePreviewUsage.error}
                        </Typography>
                      ) : (
                        <Box sx={{ mt: 0.25 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {imagePreviewUsage.completed} completed
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {imagePreviewUsage.total} total linked (any status)
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">
                        ACCESS VALIDITY
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.25 }}>
                        {formatCatalogValidityLabel(
                          imagePreviewUsage.validityDays != null
                            ? imagePreviewUsage.validityDays
                            : imagePreview.image.validity_days,
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        Applies to new bookings and assignments (0 or blank = lifetime).
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="contained" onClick={() => setImagePreview({ open: false, image: null })} sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment faces modal — card layout matches model catalog grid for visual consistency. */}
      <Dialog
        open={assignmentPreview.open}
        onClose={() => setAssignmentPreview({ open: false, row: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            py: 2.5,
            px: 3,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'primary.contrastText',
          }}
        >
          Assigned Portraits
          {assignmentPreview.row?.user?.name
            ? ` — ${assignmentPreview.row.user.name}`
            : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
          {assignmentPreview.row && assignmentPreview.row.assignments.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(auto-fill, minmax(240px, 1fr))',
                },
                gap: 2,
              }}
            >
              {assignmentPreview.row.assignments.map((a) => {
                const displayName = a.model_face_image?.display_name || `Image ${a.model_face_image_id}`;
                const refName = a.model_face_image?.name;
                const exp = a.expires_at ? new Date(a.expires_at) : null;
                const isExpired = exp && exp < new Date();
                return (
                  <Card
                    key={a.id}
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', pt: '100%', bgcolor: 'grey.50', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        image={getModelFaceImageUrl(a.model_face_image, '400x400')}
                        alt={displayName}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          p: 1,
                          zIndex: 1,
                        }}
                      />
                      {/* Origin badge — same corner language as catalog “Booked” ribbon. */}
                      <Chip
                        size="small"
                        label={a.origin === 2 ? 'Admin assign' : 'Merchant'}
                        color={a.origin === 2 ? 'primary' : 'success'}
                        variant={a.origin === 2 ? 'filled' : 'outlined'}
                        sx={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          zIndex: 2,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                        }}
                      />
                      <Chip
                        size="small"
                        label={!a.expires_at ? 'Lifetime' : isExpired ? 'Expired' : 'Active'}
                        color={!a.expires_at ? 'default' : isExpired ? 'error' : 'success'}
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          color: 'white',
                          bgcolor: !a.expires_at
                            ? 'rgba(100,116,139,0.9)'
                            : isExpired
                              ? 'error.main'
                              : 'success.main',
                          zIndex: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 2, flexGrow: 1, textAlign: 'left' }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>
                        {displayName}
                      </Typography>
                      {refName ? (
                        <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" noWrap>
                          {refName}
                        </Typography>
                      ) : null}
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: 'grey.100',
                          border: '1px dashed',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                          Access ends
                        </Typography>
                        <Tooltip
                          title={
                            a.expires_at
                              ? `Access revoked after ${new Date(a.expires_at).toLocaleString('en-GB', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit',
                                  hour12: true,
                                  timeZone: 'Asia/Kolkata',
                                })}`
                              : 'No expiry'
                          }
                        >
                          <Typography variant="body2" fontWeight={700}>
                            {a.expires_at ? formatLastInclusiveAccessDate(a.expires_at) : 'Lifetime'}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </CardContent>
                    <Divider sx={{ borderStyle: 'dashed' }} />
                    <CardActions sx={{ p: 1.5, pt: 1.5, flexDirection: 'column', gap: 0 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<PersonRemoveIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          openRevokeAssignmentConfirm(a);
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                      >
                        Revoke assignment
                      </Button>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 48, color: 'divider', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                No portraits currently assigned to this user.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, bgcolor: 'grey.100', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="contained"
            onClick={() => setAssignmentPreview({ open: false, row: null })}
            sx={{ borderRadius: 2, textTransform: 'none', px: 4, fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke assignment: explicit dialog so confirm works on top of the faces modal (no window.confirm). */}
      <Dialog
        open={revokeAssignmentConfirm.open}
        onClose={handleRevokeAssignmentCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'error.lighter',
              color: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <PersonRemoveIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Revoke assignment?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Remove{' '}
            <strong>
              {revokeAssignmentConfirm.assignment?.model_face_image?.display_name ||
                `portrait #${revokeAssignmentConfirm.assignment?.model_face_image_id || '—'}`}
            </strong>
            {revokeAssignmentConfirm.merchantName ? (
              <>
                {' '}
                from <strong>{revokeAssignmentConfirm.merchantName}</strong>
              </>
            ) : null}
            . The merchant will lose access to this catalog portrait.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleRevokeAssignmentCancel}
            disabled={revokeAssignmentConfirm.loading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRevokeAssignmentConfirm}
            disabled={revokeAssignmentConfirm.loading}
            startIcon={
              revokeAssignmentConfirm.loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PersonRemoveIcon fontSize="small" />
              )
            }
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, minWidth: 140 }}
          >
            {revokeAssignmentConfirm.loading ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>


      {/* Premium Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => !deleteConfirm.loading && setDeleteConfirm({ open: false, image: null, loading: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 1 }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Box 
            sx={{ 
              width: 64, 
              height: 64, 
              borderRadius: '50%', 
              bgcolor: 'error.lighter', 
              color: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <WarningIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Delete Model Portrait?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Are you sure you want to delete <strong>{deleteConfirm.image?.display_name}</strong>?<br/>
            This action cannot be undone.
          </Typography>
          
          <Box sx={{ border: '1px dashed', borderColor: 'divider', p: 1.5, borderRadius: 2, bgcolor: 'grey.50', mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              INTERNAL ID
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {deleteConfirm.image?.name}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 4, justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="inherit"
            onClick={() => setDeleteConfirm({ open: false, image: null, loading: false })}
            disabled={deleteConfirm.loading}
            sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteConfirm.loading}
            startIcon={deleteConfirm.loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              textTransform: 'none', 
              fontWeight: 700, 
              minWidth: 120,
              boxShadow: '0 8px 16px rgba(211, 47, 47, 0.24)',
              '&:hover': { boxShadow: '0 12px 20px rgba(211, 47, 47, 0.32)' }
            }}
          >
            {deleteConfirm.loading ? 'Deleting...' : 'Delete Forever'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FaceImagePanel = ({
  images,
  loading,
  imageViewMode,
  setImageViewMode,
  imageSearch,
  setImageSearch,
  imageGenderFilter,
  setImageGenderFilter,
  imageEthnicityFilter,
  setImageEthnicityFilter,
  imageActiveFilter,
  setImageActiveFilter,
  imageAvailabilityFilter,
  setImageAvailabilityFilter,
  imageSortBy,
  setImageSortBy,
  imageSortDir,
  setImageSortDir,
  genderOptions,
  ethnicities,
  imagePage,
  setImagePage,
  imageRowsPerPage,
  setImageRowsPerPage,
  imageTotal,
  onAddImage,
  onBulkUpload,
  onEditImage,
  onDeleteImage,
  onToggleActive,
  onPreviewImage,
  onViewAuditLogs,
}) => {
  const handleSortChange = (field) => {
    if (imageSortBy === field) {
      setImageSortDir(imageSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setImageSortBy(field);
      setImageSortDir('asc');
    }
  };

  return (
    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, description..."
              value={imageSearch}
              onChange={(e) => setImageSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                ),
                sx: { borderRadius: 2 }
              }}
            />
          </Grid>

          {/* Filters */}
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: { xs: 'flex-start', md: 'flex-end' }
              }}
            >
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Gender</InputLabel>
                <Select
                  label="Gender"
                  value={imageGenderFilter}
                  onChange={(e) => setImageGenderFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {genderOptions.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Ethnicity</InputLabel>
                <Select
                  label="Ethnicity"
                  value={imageEthnicityFilter}
                  onChange={(e) => setImageEthnicityFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {ethnicities.map((e) => (
                    <MenuItem key={e.id} value={e.id}>
                      {e.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={imageActiveFilter}
                  onChange={(e) => setImageActiveFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              {/* Filter catalog faces by merchant booking state (matches availability_status on each row). */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Availability</InputLabel>
                <Select
                  label="Availability"
                  value={imageAvailabilityFilter}
                  onChange={(e) => setImageAvailabilityFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="booked">Booked</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={`${imageSortBy}:${imageSortDir}`}
                  onChange={(e) => {
                    const [field, dir] = e.target.value.split(':');
                    setImageSortBy(field);
                    setImageSortDir(dir);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="display_name:asc">Name (A–Z)</MenuItem>
                  <MenuItem value="display_name:desc">Name (Z–A)</MenuItem>
                  <MenuItem value="sort_order:asc">Sort Order</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* View Toggle & Add Button */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={imageViewMode}
                onChange={(e, val) => val && setImageViewMode(val)}
                sx={{ 
                  bgcolor: 'grey.50',
                  '& .MuiToggleButton-root': { borderRadius: 2, px: 2 }
                }}
              >
                <ToggleButton value="grid">
                  <GridViewIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption" fontWeight="bold">Grid</Typography>
                </ToggleButton>
                <ToggleButton value="table">
                  <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption" fontWeight="bold">Table</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={onBulkUpload}
                  sx={{ borderRadius: 2, px: 3, py: 0.8, textTransform: 'none', fontWeight: 600 }}
                >
                  Bulk Upload
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onAddImage}
                  sx={{ borderRadius: 2, px: 3, py: 0.8, textTransform: 'none', fontWeight: 600 }}
                >
                  Add model
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : images.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No catalog models found.
          </Typography>
        </Box>
      ) : imageViewMode === 'grid' ? (
        <FaceImageGridView
          images={images}
          onEdit={onEditImage}
          onDelete={onDeleteImage}
          onToggleActive={onToggleActive}
          onPreview={onPreviewImage}
          onViewAuditLogs={onViewAuditLogs}
        />
      ) : (
        <FaceImageTableView
          images={images}
          onEdit={onEditImage}
          onDelete={onDeleteImage}
          onToggleActive={onToggleActive}
          onPreview={onPreviewImage}
          onViewAuditLogs={onViewAuditLogs}
          onSortChange={handleSortChange}
          sortBy={imageSortBy}
          sortDir={imageSortDir}
        />
      )}

      <TablePagination
        component="div"
        count={imageTotal}
        page={imagePage}
        onPageChange={(e, p) => setImagePage(p)}
        rowsPerPage={imageRowsPerPage}
        onRowsPerPageChange={(e) => {
          setImageRowsPerPage(parseInt(e.target.value, 10));
          setImagePage(0);
        }}
        rowsPerPageOptions={[12, 25, 50]}
      />
    </Box>
  );
};

const FaceImageGridView = ({ images, onEdit, onDelete, onToggleActive, onPreview, onViewAuditLogs }) => (
  <Grid container spacing={2} sx={{ mb: 1 }}>
    {images.map((img) => {
      // Booked catalog rows (availability_status === 2) get a distinct card treatment from available models.
      const isBooked = Number(img.availability_status) === 2;
      return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={img.id}>
        <Card 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            borderRadius: 3, 
            overflow: 'hidden',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { 
              transform: 'translateY(-4px)', 
              boxShadow: 6,
              '& .card-media-overlay': { opacity: 1 }
            },
            position: 'relative',
            ...(isBooked
              ? {
                  border: '2px solid',
                  borderColor: 'warning.main',
                  bgcolor: 'warning.lighter',
                  boxShadow: (theme) => `0 2px 16px ${alpha(theme.palette.warning.main, 0.35)}`,
                }
              : {
                  border: '1px solid',
                  borderColor: 'divider',
                })
          }}
        >
          <Box
            sx={{
              position: 'relative',
              pt: '100%',
              bgcolor: 'grey.50',
              overflow: 'hidden',
              // Subtle wash so booked thumbnails read as “reserved” at a glance.
              ...(isBooked && {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                  pointerEvents: 'none',
                  zIndex: 1,
                },
              }),
            }}
          >
            <CardMedia
              component="img"
              image={getModelFaceImageUrl(img, '400x400')}
              alt={img.display_name}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                p: 1,
                zIndex: 2,
              }}
            />
            {/* Hover preview button */}
            <Box
              className="card-media-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                cursor: 'pointer',
                zIndex: 2,
              }}
              onClick={() => onPreview(img)}
            >
              <Button 
                variant="contained" 
                size="small" 
                sx={{ borderRadius: 8, bgcolor: 'white', color: 'black', '&:hover': { bgcolor: 'grey.100' } }}
              >
                Preview
              </Button>
            </Box>
            {/* Booked ribbon: primary cue that this catalog face is merchant-reserved (not “premium”). */}
            {isBooked && (
              <Chip
                icon={<BookmarkAddedIcon sx={{ fontSize: '16px !important', color: 'inherit' }} />}
                size="small"
                label="Booked"
                color="warning"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  height: 24,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  zIndex: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              />
            )}
            {/* Status Badge */}
            <Chip
              size="small"
              label={img.is_active ? 'Active' : 'Inactive'}
              color={img.is_active ? 'success' : 'default'}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                backdropFilter: 'blur(4px)',
                bgcolor: img.is_active ? 'success.main' : 'rgba(100,116,139,0.85)',
                color: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 2,
              }}
            />
          </Box>
          
          <CardContent
            sx={{
              p: 2,
              flexGrow: 1,
              // Reinforce booked vs available in the body without fighting the image overlay.
              ...(isBooked && { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06) }),
            }}
          >
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>
                {img.display_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" sx={{ mb: 0.5 }}>
                {img.name}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, gap: 1 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ color: 'success.main' }}>
                  {img.credit_cost != null ? formatTokenAmount(img.credit_cost) : '—'}
                </Typography>
                <Chip
                  label={isBooked ? 'Booked' : 'Available'}
                  size="small"
                  color={isBooked ? 'warning' : 'default'}
                  variant={isBooked ? 'filled' : 'outlined'}
                  sx={{ height: 22, fontSize: '0.65rem', flexShrink: 0 }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {img.gender_option && (
                <Chip
                  size="small"
                  label={img.gender_option.display_name}
                  sx={{ 
                    height: 22, 
                    fontSize: '0.7rem', 
                    borderRadius: 1, 
                    bgcolor: 'primary.lighter', 
                    color: 'primary.dark',
                    fontWeight: 600
                  }}
                />
              )}
              {img.ethnicity && (
                <Chip
                  size="small"
                  label={img.ethnicity.display_name}
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    borderRadius: 1,
                    fontWeight: 500,
                    bgcolor: 'grey.100'
                  }}
                />
              )}
            </Box>
          </CardContent>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <CardActions sx={{ px: 1.5, py: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Activity log">
                <IconButton size="small" onClick={() => onViewAuditLogs(img)} sx={{ color: 'primary.main' }}>
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={img.is_active ? 'Deactivate' : 'Activate'}>
                <IconButton size="small" onClick={() => onToggleActive(img)} sx={{ color: img.is_active ? 'text.secondary' : 'success.main' }}>
                  {img.is_active ? <DisabledIcon fontSize="small" /> : <EnabledIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={() => onEdit(img)}
                sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
              >
                Edit
              </Button>
              <Tooltip title={img.purchase_count > 0 ? `Cannot delete: Assigned to ${img.purchase_count} merchant(s)` : "Delete"}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(img)}
                    disabled={img.purchase_count > 0}
                    sx={{ 
                      bgcolor: 'error.lighter', 
                      '&:hover': { bgcolor: 'error.light', color: 'error.dark' },
                      '&.Mui-disabled': { bgcolor: 'grey.100', color: 'grey.400' }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </CardActions>
        </Card>
      </Grid>
      );
    })}
  </Grid>
);

const FaceImageTableView = ({
  images,
  onEdit,
  onDelete,
  onToggleActive,
  onPreview,
  onViewAuditLogs,
  onSortChange,
  sortBy,
  sortDir,
}) => (
  <TableContainer sx={{ mb: 1 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Preview</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Gender</TableCell>
          <TableCell>Ethnicity</TableCell>
          <TableCell>Tokens</TableCell>
          <TableCell>Availability</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {images.map((img) => (
          <TableRow key={img.id} hover>
            <TableCell>
              <Avatar
                src={getModelFaceImageUrl(img)}
                variant="rounded"
                sx={{ width: 48, height: 48, cursor: 'pointer' }}
                onClick={() => onPreview(img)}
              />
            </TableCell>
            <TableCell>
              <Typography variant="body2" fontWeight="bold">
                {img.display_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {img.name}
              </Typography>
            </TableCell>
            <TableCell>{img.gender_option?.display_name || '—'}</TableCell>
            <TableCell>
              {img.ethnicity ? (
                <Chip
                  label={img.ethnicity.display_name}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                />
              ) : '—'}
            </TableCell>
            <TableCell>
              <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                {img.credit_cost != null ? formatTokenAmount(img.credit_cost) : '—'}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip
                label={img.availability_status === 2 ? 'Booked' : 'Available'}
                size="small"
                color={img.availability_status === 2 ? 'warning' : 'default'}
                variant={img.availability_status === 2 ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: '0.65rem' }}
              />
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                label={img.is_active ? 'Active' : 'Inactive'}
                color={img.is_active ? 'success' : 'default'}
              />
            </TableCell>
            <TableCell align="right">
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Tooltip title="Activity log">
                  <IconButton size="small" onClick={() => onViewAuditLogs(img)} sx={{ color: 'primary.main' }}>
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={img.is_active ? 'Deactivate' : 'Activate'}>
                  <IconButton size="small" onClick={() => onToggleActive(img)} sx={{ color: img.is_active ? 'text.secondary' : 'success.main' }}>
                    {img.is_active ? <DisabledIcon fontSize="small" /> : <EnabledIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => onEdit(img)} sx={{ color: 'text.secondary' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <Tooltip title={img.purchase_count > 0 ? `Cannot delete: Assigned to ${img.purchase_count} merchant(s)` : "Delete"}>
                  <span>
                    <IconButton 
                      size="small" 
                      color="error" 
                      disabled={img.purchase_count > 0}
                      onClick={() => onDelete(img)} 
                      sx={{ 
                        bgcolor: 'error.lighter', 
                        '&:hover': { bgcolor: 'error.light' },
                        '&.Mui-disabled': { bgcolor: 'grey.100', color: 'grey.400' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const UserAssignmentsPanel = ({
  loading,
  assignmentsByUser,
  assignPage,
  setAssignPage,
  assignRowsPerPage,
  setAssignRowsPerPage,
  assignTotal,
  assignmentSearchInput,
  setAssignmentSearchInput,
  assignmentSearchActive,
  onOpenAssignDialog,
  onOpenFacesModal,
}) => (
  <Box sx={{ px: 2, pt: 2, pb: 1 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
      <Typography variant="subtitle1" fontWeight="bold">
        User Assignments
      </Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenAssignDialog}>
        Assign User
      </Button>
    </Box>
    {/* Filters inventory rows by merchant identity (includes mobile / WhatsApp). */}
    <TextField
      fullWidth
      size="small"
      placeholder="Search by name, email, company, mobile, or WhatsApp…"
      value={assignmentSearchInput}
      onChange={(e) => setAssignmentSearchInput(e.target.value)}
      sx={{ mb: 2, maxWidth: { sm: 480 } }}
      InputProps={{
        startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
        sx: { borderRadius: 2, bgcolor: 'background.paper' },
      }}
    />

	    <TableContainer>
	      <Table size="small">
	        <TableHead>
	          <TableRow>
	            <TableCell>User</TableCell>
	            <TableCell>Contact</TableCell>
	            <TableCell>Company</TableCell>
	            <TableCell>Assignments</TableCell>
	            <TableCell align="right">Actions</TableCell>
	          </TableRow>
	        </TableHead>
	        <TableBody>
	          {loading ? (
	            <TableRow>
	              <TableCell colSpan={5} align="center">
	                <CircularProgress size={24} />
	              </TableCell>
	            </TableRow>
	          ) : assignmentsByUser.length === 0 ? (
	            <TableRow>
	              <TableCell colSpan={5} align="center">
	                {assignmentSearchActive ? 'No assignments match your search.' : 'No user assignments found'}
	              </TableCell>
	            </TableRow>
	          ) : (
	            assignmentsByUser.map((row) => (
	              <UserAssignmentRow
	                key={row.user_id}
	                row={row}
	                onOpenFacesModal={onOpenFacesModal}
	              />
	            ))
	          )}
	        </TableBody>
	      </Table>
    </TableContainer>

    <TablePagination
      component="div"
      count={assignTotal}
      page={assignPage}
      onPageChange={(e, p) => setAssignPage(p)}
      rowsPerPage={assignRowsPerPage}
      onRowsPerPageChange={(e) => {
        setAssignRowsPerPage(parseInt(e.target.value, 10));
        setAssignPage(0);
      }}
      rowsPerPageOptions={[12, 25, 50]}
    />
  </Box>
);

const UserAssignmentRow = ({ row, onOpenFacesModal }) => {
  // If the merchant account was removed but inventory row still exists, flag it clearly in UI.
  const isDeletedUserRecord = !row.user;
  const contactEmail = row.email || row.user?.email || null;
  const contactMobile =
    (row.user?.mobile_code && row.user?.mobile
      ? `${row.user.mobile_code} ${row.user.mobile}`
      : null) ||
    (row.user?.whatsapp_code && row.user?.whatsapp_number
      ? `${row.user.whatsapp_code} ${row.user.whatsapp_number}`
      : null) ||
    row.user?.mobile ||
    row.user?.whatsapp_number ||
    null;
  return (
  <TableRow hover>
    <TableCell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">{row.user?.name || `User ${row.user_id}`}</Typography>
        {isDeletedUserRecord && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="Deleted user"
          />
        )}
      </Box>
    </TableCell>
    <TableCell>
      <Typography variant="body2">{contactEmail || '—'}</Typography>
      {contactMobile && (
        <Typography variant="caption" color="text.secondary" display="block">{contactMobile}</Typography>
      )}
    </TableCell>
    <TableCell>{row.company_name || '—'}</TableCell>
    <TableCell>
      <Typography variant="body2" fontWeight={600}>
        {row.assignments.length}
      </Typography>
    </TableCell>
    <TableCell align="right">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
        <Tooltip title="View assigned portraits">
          <IconButton
            size="small"
            color="primary"
            aria-label="View assigned portraits"
            onClick={() => onOpenFacesModal(row)}
            sx={{
              border: '1px solid',
              borderColor: 'primary.light',
              borderRadius: 1.5,
              '&:hover': { bgcolor: 'primary.lighter' },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  </TableRow>
  );
};

const UserConfigPanel = ({
  loading,
  enabledUsers,
  configPage,
  setConfigPage,
  configRowsPerPage,
  setConfigRowsPerPage,
  configTotal,
  merchantAddonSearchInput,
  setMerchantAddonSearchInput,
  merchantAddonSearchActive,
  onOpenEnableDialog,
}) => (
  <Box sx={{ px: 2, pt: 2, pb: 1 }}>
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: (theme) =>
          `linear-gradient(180deg, ${theme.palette.grey[50]} 0%, ${theme.palette.background.paper} 100%)`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
          >
            <StarIcon sx={{ color: 'warning.main' }} />
            Merchant addon management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Enable AI Model Studio, customer photo, and other addons per merchant. Use Edit on a row to add or remove
            services.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`${configTotal} merchant${configTotal === 1 ? '' : 's'} with addons`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => onOpenEnableDialog()}
          sx={{ borderRadius: 2, textTransform: 'none', px: 2.5, py: 1, fontWeight: 700, boxShadow: 2 }}
        >
          Configure merchant
        </Button>
      </Box>
      {/* Server-side search: name, email, or company (see GET /model-face/enabled-users?search=). */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by name, email, company, mobile, or WhatsApp…"
        value={merchantAddonSearchInput}
        onChange={(e) => setMerchantAddonSearchInput(e.target.value)}
        sx={{ mt: 2, maxWidth: { sm: 480 } }}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          sx: { borderRadius: 2, bgcolor: 'background.paper' },
        }}
      />
    </Paper>

    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      }}
    >
      <Table size="small" sx={{ '& tbody tr:hover': { bgcolor: 'action.hover' } }}>
        <TableHead sx={{ bgcolor: 'grey.100' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>User</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Contact</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Company</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Active services</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                <CircularProgress size={32} thickness={4} />
              </TableCell>
            </TableRow>
          ) : enabledUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                <Typography color="text.secondary">
                  {merchantAddonSearchActive
                    ? 'No merchants match your search.'
                    : 'No users have specialized services enabled'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            enabledUsers.map((item) => (
              <TableRow key={item.user_id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'primary.light', fontSize: '0.875rem' }}>
                      {item.user?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.user?.name || `User ${item.user_id}`}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{item.user?.email || '—'}</Typography>
                  {(() => {
                    const mob =
                      (item.user?.mobile_code && item.user?.mobile
                        ? `${item.user.mobile_code} ${item.user.mobile}`
                        : null) ||
                      (item.user?.whatsapp_code && item.user?.whatsapp_number
                        ? `${item.user.whatsapp_code} ${item.user.whatsapp_number}`
                        : null) ||
                      item.user?.mobile ||
                      item.user?.whatsapp_number ||
                      null;
                    return mob ? (
                      <Typography variant="caption" color="text.secondary" display="block">{mob}</Typography>
                    ) : null;
                  })()}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{item.user?.company_name || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {/* Read-only chips (no delete affordance); use Edit to add or remove services. */}
                    {item.enabled_services?.map(s => (
                      <Chip 
                        key={s.id} 
                        label={s.addon_service?.name || s.service_type} 
                        size="small" 
                        sx={{ 
                          height: 24, 
                          fontSize: '0.75rem',
                          bgcolor: s.service_type === 'model_face' ? 'primary.50' : 'success.50',
                          color: s.service_type === 'model_face' ? 'primary.700' : 'success.700',
                          border: '1px solid',
                          borderColor: s.service_type === 'model_face' ? 'primary.100' : 'success.100',
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {/* Opens the same dialog with this merchant pre-selected and checkboxes synced to current services. */}
                  <Tooltip title="Edit addon services">
                    <IconButton size="small" color="primary" onClick={() => onOpenEnableDialog(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[12, 25, 50]}
        component="div"
        count={configTotal}
        rowsPerPage={configRowsPerPage}
        page={configPage}
        onPageChange={(e, p) => setConfigPage(p)}
        onRowsPerPageChange={(e) => {
          setConfigRowsPerPage(parseInt(e.target.value, 10));
          setConfigPage(0);
        }}
      />
    </TableContainer>
  </Box>
);

const AssignUserModal = ({
  open,
  onClose,
  assignForm,
  setAssignForm,
  userSearchResults,
  userSearchLoading,
  searchUsers,
  setUserSearchResults,
  setUserSearchLoading,
  allImages,
  onReview,
}) => {
  const selectedUserId = assignForm.user_id?.id || assignForm.user_id;

  // Only show portraits the admin can actually assign:
  // - if no merchant is selected: hide globally booked models
  // - if a merchant is selected: allow globally booked models only when booked_by_user_id matches
  const visibleImages = useMemo(() => {
    const selected = selectedUserId;
    if (!selected) {
      return (allImages || []).filter((img) => img?.availability_status !== 2);
    }

    return (allImages || []).filter((img) => {
      if (img?.availability_status !== 2) return true;
      return String(img?.booked_by_user_id ?? '') === String(selected);
    });
  }, [allImages, selectedUserId]);

  const handleToggleImage = (imageId) => {
    const current = assignForm.model_face_image_ids || [];
    const exists = current.includes(imageId);
    const updated = exists ? current.filter((id) => id !== imageId) : [...current, imageId];
    setAssignForm({ ...assignForm, model_face_image_ids: updated });
  };

  // UI aligned with “Configure merchant addons”: same header style, controlled Autocomplete, phone-aware labels.
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          py: 2.5,
          px: 3,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.contrastText',
        }}
      >
        Assign portraits to user
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search for a merchant (name, email, mobile, or WhatsApp), then choose catalog portraits to assign.
            </Typography>
            <Typography variant="overline" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
              Merchant
            </Typography>
            <Autocomplete
              options={userSearchResults}
              value={assignForm.user_id || null}
              isOptionEqualToValue={(a, b) => !!a && !!b && String(a.id) === String(b.id)}
              getOptionLabel={formatMerchantAutocompleteLabel}
              loading={userSearchLoading}
              onInputChange={(e, val) => searchUsers(val, setUserSearchResults, setUserSearchLoading)}
              onChange={(e, val) => setAssignForm({ ...assignForm, user_id: val })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search merchant"
                  placeholder="Name, email, mobile, or WhatsApp…"
                  sx={{ bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {userSearchLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography variant="overline" color="text.secondary" fontWeight="bold">
                Available Portraits
              </Typography>
              <Chip 
                label={`${assignForm.model_face_image_ids.length} Selected`} 
                size="small" 
                color="primary" 
                sx={{ fontWeight: 'bold', borderRadius: 1.5 }} 
              />
            </Box>
            {/* Same card proportions as model catalog grid (square thumb + footer). */}
            <Box
              sx={{
                maxHeight: 440,
                overflowY: 'auto',
                pr: 0.5,
                py: 0.5,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(auto-fill, minmax(200px, 1fr))',
                },
                gap: 2,
                alignContent: 'start',
              }}
            >
              {visibleImages.map((img) => {
                const selected = assignForm.model_face_image_ids.includes(img.id);
                const isBooked = Number(img.availability_status) === 2;
                return (
                  <Card
                    key={img.id}
                    elevation={0}
                    onClick={() => handleToggleImage(img.id)}
                    sx={{
                      position: 'relative',
                      borderRadius: 3,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: selected ? (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: 4,
                        borderColor: 'primary.main',
                      },
                      ...(isBooked && !selected && {
                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.04),
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        pt: '100%',
                        bgcolor: 'grey.50',
                        overflow: 'hidden',
                        ...(isBooked && {
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                            pointerEvents: 'none',
                            zIndex: 1,
                          },
                        }),
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={getModelFaceImageUrl(img, '400x400')}
                        alt={img.display_name}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          p: 1,
                          zIndex: 2,
                        }}
                      />
                      <Checkbox
                        size="small"
                        checked={selected}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleImage(img.id);
                        }}
                        inputProps={{ 'aria-label': `Select ${img.display_name}` }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          p: 0.5,
                          color: 'primary.main',
                          bgcolor: 'rgba(255,255,255,0.92)',
                          borderRadius: 1,
                          zIndex: 3,
                          boxShadow: 1,
                          '&:hover': { bgcolor: 'white' },
                        }}
                      />
                      {isBooked && (
                        <Chip
                          icon={<BookmarkAddedIcon sx={{ fontSize: '14px !important', color: 'inherit' }} />}
                          size="small"
                          label="Booked"
                          color="warning"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            height: 22,
                            fontSize: '0.6rem',
                            fontWeight: 800,
                            zIndex: 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                          }}
                        />
                      )}
                      {selected && (
                        <Chip
                          size="small"
                          label="Selected"
                          color="primary"
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            height: 22,
                            fontSize: '0.65rem',
                            fontWeight: 800,
                            zIndex: 3,
                          }}
                        />
                      )}
                      <Chip
                        size="small"
                        label={img.credit_cost != null ? formatTokenAmount(img.credit_cost) : '—'}
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          zIndex: 3,
                          bgcolor: 'rgba(0,0,0,0.72)',
                          color: 'common.white',
                        }}
                      />
                    </Box>
                    <CardContent
                      sx={{
                        p: 1.75,
                        textAlign: 'left',
                        ...(selected && {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                        }),
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ lineHeight: 1.25 }}>
                        {img.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" noWrap>
                        {img.name}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {img.gender_option && (
                          <Chip
                            size="small"
                            label={img.gender_option.display_name}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: 'primary.lighter',
                              color: 'primary.dark',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {img.ethnicity && (
                          <Chip
                            size="small"
                            label={img.ethnicity.display_name}
                            sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'grey.100', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, bgcolor: 'grey.100', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onReview}
          disabled={!assignForm.user_id || assignForm.model_face_image_ids.length === 0}
          sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
        >
          Review token total &amp; assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddonServices;
